import { useCallback, useEffect, useRef, useState } from 'react';
import { ApprovedGameGoals } from 'shared/interfaces/approved-game-goals/approved-game-goals.interface';
import { gameApi } from 'src/api/game/game';
import { markPwaMeaningfulEarn } from 'src/pwa/pwa';
import { pushRepeatableDataLayerEvent } from 'src/utils/gtm';
import { GtmEventName } from 'src/utils/gtm-event-name.enum';

interface UseApprovedGameGoalsOptions {
  onPayoutComplete?: (paidGoals: ApprovedGameGoals | null) => void | Promise<void>;
  shouldTrackOfferGoalCompleted?: boolean;
}

export const useApprovedGameGoals = ({
  onPayoutComplete,
  shouldTrackOfferGoalCompleted,
}: UseApprovedGameGoalsOptions = {}) => {
  const [approvedGoals, setApprovedGoals] = useState<ApprovedGameGoals | null>(null);
  const [isPayingGoals, setIsPayingGoals] = useState(false);
  const [payoutFailed, setPayoutFailed] = useState(false);
  const payoutInFlightRef = useRef(false);
  const hasTrackedApprovedGoalsRef = useRef(false);

  const loadApprovedGoals = useCallback(async () => {
    try {
      setApprovedGoals(await gameApi.getApprovedGameGoals());
    } catch {
      setApprovedGoals(null);
    }
  }, []);

  useEffect(() => {
    void loadApprovedGoals();
  }, [loadApprovedGoals]);

  useEffect(() => {
    if (!approvedGoals) {
      hasTrackedApprovedGoalsRef.current = false;
      return;
    }

    if (!shouldTrackOfferGoalCompleted || hasTrackedApprovedGoalsRef.current) {
      return;
    }

    hasTrackedApprovedGoalsRef.current = true;
    markPwaMeaningfulEarn('offer_goal_completed');
    pushRepeatableDataLayerEvent(GtmEventName.OfferGoalCompleted);
  }, [approvedGoals, shouldTrackOfferGoalCompleted]);

  useEffect(() => {
    if (!approvedGoals || payoutInFlightRef.current) {
      return;
    }

    payoutInFlightRef.current = true;
    setIsPayingGoals(true);
    setPayoutFailed(false);
    let payoutResult: ApprovedGameGoals | null = null;

    void gameApi
      .payApprovedGameGoals()
      .then((paidGoals) => {
        payoutResult = paidGoals;
        if (paidGoals) {
          setApprovedGoals(paidGoals);
        }
      })
      .catch(() => {
        setPayoutFailed(true);
      })
      .finally(() => {
        setIsPayingGoals(false);
        void onPayoutComplete?.(payoutResult);
      });
  }, [approvedGoals, onPayoutComplete]);

  const dismissApprovedGoals = useCallback(() => {
    setApprovedGoals(null);
  }, []);

  return {
    approvedGoals,
    isPayingGoals,
    payoutFailed,
    loadApprovedGoals,
    dismissApprovedGoals,
  };
};
